/**
 * Staff session for the support inbox. See support/docs/identity-contract.md.
 *
 * Staff sign in with Auth0 (verified email OTP or Google). That ID token authenticates edge-function
 * calls directly. Direct table reads and realtime need a *Supabase* token so
 * RLS can see `auth.uid()`; `auth-exchange` mints one for staff roles and
 * returns the caller's profile. Both are cached here and refreshed before
 * they expire.
 */

export interface StaffProfile {
  id: string;
  role: string;
  organization_id: string | null;
  display_name: string | null;
  email: string | null;
}

interface Exchange {
  profile: StaffProfile;
  accessToken: string | null;
  expiresAt: number | null; // epoch seconds
}

const API_URL = import.meta.env.VITE_SUPABASE_URL || import.meta.env.NEXT_PUBLIC_SUPABASE_URL || '';
const REFRESH_MARGIN_SECONDS = 120;

let idTokenGetter: (() => Promise<string | null>) | null = null;
let current: Exchange | null = null;
let inflight: Promise<Exchange> | null = null;
let generation = 0;

/** Called by the Auth0 provider once a user is signed in (null on sign-out). */
export function setIdTokenGetter(getter: (() => Promise<string | null>) | null): void {
  generation += 1;
  idTokenGetter = getter;
  current = null;
  inflight = null;
}

export async function getIdToken(): Promise<string | null> {
  return idTokenGetter ? idTokenGetter() : null;
}

async function exchange(): Promise<Exchange> {
  const idToken = await getIdToken();
  if (!idToken) throw new Error('Not signed in');
  const res = await fetch(`${API_URL}/functions/v1/auth-exchange`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Sign-in exchange failed (${res.status})`);
  return { profile: body.profile, accessToken: body.access_token, expiresAt: body.expires_at };
}

function fresh(e: Exchange | null): e is Exchange {
  if (!e) return false;
  if (!e.expiresAt) return true; // no Supabase token to expire (non-staff)
  return e.expiresAt - REFRESH_MARGIN_SECONDS > Date.now() / 1000;
}

export async function getExchange(): Promise<Exchange> {
  if (fresh(current)) return current;
  if (!inflight) {
    const requestedGeneration = generation;
    inflight = exchange()
      .then((e) => {
        if (requestedGeneration !== generation) throw new Error('Sign-in session changed. Please try again.');
        current = e;
        return e;
      })
      .finally(() => {
        if (requestedGeneration === generation) inflight = null;
      });
  }
  return inflight;
}

/** For supabase-js `accessToken`: null makes the client fall back to anon. */
export async function getSupabaseAccessToken(): Promise<string | null> {
  if (!idTokenGetter) return null;
  try {
    return (await getExchange()).accessToken;
  } catch (err) {
    console.error('Supabase session unavailable:', err);
    return null;
  }
}

export function currentProfile(): StaffProfile | null {
  return current?.profile ?? null;
}
